import { useQuery } from '@apollo/client';
import { FileAttachmentsWidget } from '@procureai/ui-modules-mui';
import { useTranslation } from 'react-i18next';

import { FileAttachmentStatus } from '../FileAttachmentsWidget/FileAttachmentsWidget.types';
import {
  classificationOptions,
  FileAttachmentsWidgetEditFeature,
  getAttachmentFragments,
  useFileAttachmentsUpdateMutation,
} from '../FileAttachmentsWidget/FileAttachmentsWidget.util';
import { useNegotiationDetail } from '../Module.hooks';
import { TsrctAttachmentParentVariant, TsrctUploadedByVariant } from '@/generated/gql/graphql';
import { I18nNamespace } from '@/legacy/common/types/I18nNamespace';
import { isNegotiationCaseEditable } from '@/legacy/util/negotiationCase/isNegotiationCaseEditable';

import {
  NEGOTIATION_CASE_INTERNAL_FILE_ATTACHMENTS_CASE_AND_DEMAND_QUERY,
  NEGOTIATION_CASE_INTERNAL_FILE_ATTACHMENTS_QUERY,
} from './NegotiationCaseInternalFileAttachmentsWidget.gql';

export const NegotiationCaseInternalFileAttachmentsWidget = () => {
  const { negotiationId } = useNegotiationDetail();
  const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'NegotiationCaseInternalFileAttachmentsWidget' });
  const negotiationCaseParent = {
    id: negotiationId,
    variant: TsrctAttachmentParentVariant.NEGOTIATION_CASE,
  };

  const { data: caseAndDemandData, loading: caseAndDemandLoading } = useQuery(
    NEGOTIATION_CASE_INTERNAL_FILE_ATTACHMENTS_CASE_AND_DEMAND_QUERY,
    {
      variables: {
        negotiationCaseId: negotiationId!,
      },
    },
  );

  const variables = {
    parents: [
      negotiationCaseParent,
      {
        variant: TsrctAttachmentParentVariant.DEMAND,
        id: caseAndDemandData?.tesseract.negotiationCase.demand.id,
      },
    ],
    filter: {
      uploadedByVariants: [TsrctUploadedByVariant.INTERNAL_USER],
    },
  };
  const { data, loading: attachmentsLoading } = useQuery(NEGOTIATION_CASE_INTERNAL_FILE_ATTACHMENTS_QUERY, {
    variables,
    skip: !caseAndDemandData?.tesseract.negotiationCase.demand.id,
  });

  const { save, saving } = useFileAttachmentsUpdateMutation({
    refetchQueries: [{ query: NEGOTIATION_CASE_INTERNAL_FILE_ATTACHMENTS_QUERY, variables }],
    parent: negotiationCaseParent,
  });

  const loading = caseAndDemandLoading || attachmentsLoading;

  const attachments = getAttachmentFragments(data?.tesseract.attachments);

  return (
    <>
      <FileAttachmentsWidget
        editFeatures={[FileAttachmentsWidgetEditFeature.UPLOAD, FileAttachmentsWidgetEditFeature.CHANGE_VISIBILITY]}
        uploadButtonLabel={t('upload.buttonLabel')}
        attachments={attachments}
        loading={loading}
        onSave={save}
        saving={saving}
        getItemStatus={(attachment) =>
          attachment.visibilities.some((v) => v.id === negotiationId)
            ? FileAttachmentStatus.VISIBLE
            : FileAttachmentStatus.HIDDEN
        }
        isItemDeletable={(attachment) => attachment.parent.id === negotiationId}
        isEditable={isNegotiationCaseEditable(caseAndDemandData?.tesseract.negotiationCase) && !loading}
        classificationOptions={classificationOptions}
        getItemClassification={(attachment) => attachment.classification}
      />
    </>
  );
};
